import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AppUnitOption {
  value: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-price-unit-input',
  templateUrl: './app-price-unit-input.component.html',
  styleUrls: ['./app-price-unit-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppPriceUnitInputComponent),
      multi: true,
    },
  ],
})
export class AppPriceUnitInputComponent implements ControlValueAccessor, OnChanges {
  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  get resolvedLabel(): string {
    return this.labelText || this.label || '';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  @Input() placeholder: string = '0.00';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() helperText: string = '';

  private _unitOptions: any[] = [];
  @Input()
  get unitOptions(): any[] {
    return this._unitOptions;
  }
  set unitOptions(val: any[]) {
    this._unitOptions = val ? [...val] : [];
    this.formatUnitOptions();
    this.cdr.markForCheck();
  }

  @Input() unitValue: any = null;
  @Input() unitPlaceholder: string = 'Unit';
  @Input() searchable: boolean = true;
  @Input() searchPlaceholder: string = 'Search unit...';

  @Input() size: 'sm' | 'md' | 'lg' | 'compact' = 'md';
  @Input() height: number | string = '';
  @Input() customClass: string = '';
  @Input() containerClass: string = '';

  @Output() priceChange = new EventEmitter<string>();
  @Output() unitChange = new EventEmitter<any>();
  @Output() unitSearchChange = new EventEmitter<string>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();

  value: string = '';
  isFocused: boolean = false;
  isUnitDropdownOpen: boolean = false;
  unitSearchTerm: string = '';
  formattedUnitOptions: AppUnitOption[] = [];

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unitOptions'] || changes['unitValue']) {
      this.formatUnitOptions();
      this.cdr.markForCheck();
    }
  }

  private formatUnitOptions(): void {
    this.formattedUnitOptions = this.formatOptionsList(this._unitOptions);
  }

  private formatOptionsList(rawList: any[]): AppUnitOption[] {
    if (!Array.isArray(rawList)) {
      return [];
    }

    return rawList.map((opt) => {
      if (opt !== null && typeof opt === 'object') {
        const label =
          opt.label ||
          opt.uom_primary_code ||
          opt.uom_name ||
          opt.uom_code ||
          opt.uom_short_name ||
          opt.primary_code ||
          opt.unit_name ||
          opt.name ||
          opt.code ||
          (opt.value !== undefined ? String(opt.value) : '');

        const value =
          opt.value !== undefined
            ? opt.value
            : opt.uom_sl_no !== undefined
            ? opt.uom_sl_no
            : opt.id !== undefined
            ? opt.id
            : opt;

        return {
          value: value,
          label: String(label || value || ''),
          disabled: !!opt.disabled,
        };
      }
      return {
        value: opt,
        label: String(opt),
      };
    });
  }

  writeValue(val: any): void {
    this.value = val !== undefined && val !== null ? String(val) : '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isUnitDropdownOpen = false;
    }
    this.cdr.markForCheck();
  }

  onInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    let inputVal = inputEl.value || '';

    // Decimal validation: allows digits and max 2 decimal places
    const match = inputVal.match(/^\d*(\.\d{0,2})?/);
    inputVal = match ? match[0] : '';
    inputEl.value = inputVal;

    this.value = inputVal;
    this.onChange(inputVal);
    this.priceChange.emit(inputVal);
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.inputFocus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.inputBlur.emit(event);
  }

  toggleUnitDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) return;
    this.isUnitDropdownOpen = !this.isUnitDropdownOpen;
    if (this.isUnitDropdownOpen) {
      this.unitSearchTerm = '';
      this.formatUnitOptions();
    }
    this.cdr.markForCheck();
  }

  selectUnit(opt: AppUnitOption, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (opt.disabled) return;

    this.unitValue = opt.value;
    this.unitChange.emit(opt.value);
    this.isUnitDropdownOpen = false;
    this.cdr.markForCheck();
  }

  onUnitSearchInput(event: any): void {
    this.unitSearchTerm = event.target.value || '';
    this.unitSearchChange.emit(this.unitSearchTerm);
    this.cdr.markForCheck();
  }

  get selectedUnitLabel(): string {
    if (this.unitValue === null || this.unitValue === undefined || this.unitValue === '') {
      return this.unitPlaceholder;
    }
    const opts = this.formattedUnitOptions.length
      ? this.formattedUnitOptions
      : this.formatOptionsList(this._unitOptions);

    const matched = opts.find(
      (u) => u.value === this.unitValue || String(u.value) === String(this.unitValue)
    );
    return matched ? matched.label : this.unitPlaceholder;
  }

  get filteredUnitOptions(): AppUnitOption[] {
    const opts = this.formattedUnitOptions.length
      ? this.formattedUnitOptions
      : this.formatOptionsList(this._unitOptions);

    if (!this.unitSearchTerm || !this.searchable) {
      return opts;
    }
    const term = this.unitSearchTerm.toLowerCase();
    return opts.filter((opt) =>
      opt.label && opt.label.toLowerCase().includes(term)
    );
  }

  get hasError(): boolean {
    return !!this.errorMsg && this.errorMsg.trim().length > 0;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isUnitDropdownOpen) {
        this.isUnitDropdownOpen = false;
        this.cdr.markForCheck();
      }
    }
  }
}
